import React, { useEffect, useState } from 'react';
import { X, Send, AlertTriangle, Info, CheckCircle2, CalendarPlus } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from './LoadingSpinner';

const formatDate = (s) => (s ? new Date(s + 'T00:00:00').toLocaleDateString('pl-PL') : '');

/** Okno składania wniosku urlopowego z podglądem walidacji z KS-ZZL na żywo. */
const LeaveRequestModal = ({ isOpen, onClose, onSubmitted }) => {
  const [kinds, setKinds] = useState([]);
  const [kindCode, setKindCode] = useState('UWP');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [comment, setComment] = useState('');
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [supervisor, setSupervisor] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setKindCode('UWP');
    setDateFrom('');
    setDateTo('');
    setComment('');
    setValidation(null);
    setSupervisor(null);
    api.get('/kadry/urlopy/types')
      .then((res) => setKinds(res.data || []))
      .catch(() => setKinds([]));
    api.get('/kadry/urlopy/me/supervisor')
      .then((res) => setSupervisor(res.data))
      .catch(() => setSupervisor({ source: 'NONE' }));
  }, [isOpen]);

  const noSupervisor = supervisor?.source === 'NONE';

  // Podgląd walidacji (limit, nakładanie, zamknięty okres) z opóźnieniem po każdej zmianie.
  useEffect(() => {
    if (!isOpen || !kindCode || !dateFrom || !dateTo) {
      setValidation(null);
      return undefined;
    }
    const t = setTimeout(async () => {
      setValidating(true);
      try {
        const res = await api.post('/kadry/urlopy/me/validate', { kindCode, dateFrom, dateTo });
        setValidation(res.data);
      } catch (err) {
        const msg = err.response?.data?.error || 'Nie udało się sprawdzić wniosku.';
        setValidation({ ok: false, errors: [msg], warnings: [], overlaps: [] });
      } finally {
        setValidating(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [isOpen, kindCode, dateFrom, dateTo]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validation?.ok) return;
    setSubmitting(true);
    try {
      await api.post('/kadry/urlopy/me', { kindCode, dateFrom, dateTo, comment: comment || null });
      toast.success('Wniosek złożony. Kadry otrzymały powiadomienie.');
      if (onSubmitted) onSubmitted();
      onClose();
    } catch (err) {
      const data = err.response?.data;
      const details = data?.details?.length ? ` ${data.details.join(' ')}` : '';
      toast.error((data?.error || 'Nie udało się złożyć wniosku.') + details);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" aria-label="Zamknij">
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center">
              <CalendarPlus className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Wniosek urlopowy</h2>
              <p className="text-xs text-gray-500">Podpisuje przełożony, potem zatwierdzają kadry</p>
            </div>
          </div>

          {supervisor && (
            noSupervisor ? (
              <div className="mb-4 flex items-start gap-2 rounded-lg px-3 py-2 text-sm bg-red-50 text-red-800 border border-red-200">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Nie masz przypisanego przełożonego, więc nie możesz złożyć wniosku. Skontaktuj się z kadrami.</span>
              </div>
            ) : (
              <div className="mb-4 flex items-start gap-2 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-700 border border-gray-100">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
                <span>Wniosek trafi do podpisu: <b>{supervisor.supervisorName}</b></span>
              </div>
            )
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="leaveKind" className="block text-sm text-gray-700 mb-1.5">Rodzaj nieobecności</label>
              <select
                id="leaveKind"
                value={kindCode}
                onChange={(e) => setKindCode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                {kinds.map((k) => (
                  <option key={k.code} value={k.code}>{k.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="leaveFrom" className="block text-sm text-gray-700 mb-1.5">Od</label>
                <input
                  id="leaveFrom"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); if (!dateTo || e.target.value > dateTo) setDateTo(e.target.value); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="leaveTo" className="block text-sm text-gray-700 mb-1.5">Do</label>
                <input
                  id="leaveTo"
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="leaveComment" className="block text-sm text-gray-700 mb-1.5">
                Komentarz <span className="text-gray-400 text-xs">(opcjonalny)</span>
              </label>
              <textarea
                id="leaveComment"
                rows={2}
                maxLength={200}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="np. wyjazd rodzinny"
              />
            </div>

            {validating && (
              <div className="flex items-center gap-2 text-sm text-gray-500"><LoadingSpinner size="sm" /> Sprawdzam w KS-ZZL…</div>
            )}

            {validation && !validating && (
              <div className="space-y-2">
                {validation.ok && (
                  <div className="flex items-start gap-2 rounded-lg px-3 py-2 text-sm bg-emerald-50 text-emerald-800 border border-emerald-200">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Wniosek można złożyć.
                      {validation.workingDays != null && <> Dni robocze wg kalendarza: <b>{validation.workingDays}</b>.</>}
                    </span>
                  </div>
                )}
                {(validation.errors || []).map((e, i) => (
                  <div key={`e${i}`} className="flex items-start gap-2 rounded-lg px-3 py-2 text-sm bg-red-50 text-red-800 border border-red-200">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{e}</span>
                  </div>
                ))}
                {(validation.overlaps || []).map((o, i) => (
                  <div key={`o${i}`} className="text-xs text-red-700 pl-6">
                    {o.source}: {o.typeName} {formatDate(o.dateFrom)} – {formatDate(o.dateTo)}
                  </div>
                ))}
                {(validation.warnings || []).map((w, i) => (
                  <div key={`w${i}`} className="flex items-start gap-2 rounded-lg px-3 py-2 text-sm bg-amber-50 text-amber-800 border border-amber-200">
                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{w}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="btn btn-secondary">Anuluj</button>
              <button
                type="submit"
                disabled={submitting || validating || !validation?.ok || noSupervisor}
                className="btn btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? <LoadingSpinner size="sm" /> : <Send className="w-4 h-4" />}
                Złóż wniosek
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LeaveRequestModal;
